import React, { createContext, useState, useEffect } from "react";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  OAuthProvider,
} from "firebase/auth";
import app from "../services/firebase";
import axios from "../services/axios";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";

const auth = getAuth(app);

export const AuthContext = createContext();

export default function AuthContextProvider({ children }) {
  const [user, setU] = useState(() => {
    const user = localStorage.getItem("SPICY_USER");
    return user ? JSON.parse(user) : null;
  });
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const setUser = (user) => {
    if (user && typeof user === "object") {
      user.isPremium = false;
      if (user?.stripe_subscription_status) {
        user.isPremium = ["active", "trianling"].includes(
          user.stripe_subscription_status
        );
      }
      if (user?.premium) {
        user.isPremium = user.premium;
      }
    }

    setU(user);
    localStorage.setItem("SPICY_USER", JSON.stringify(user));
  };

  const signInWithGoogle = () => {
    return new Promise((resolve, reject) => {
      const provider = new GoogleAuthProvider();
      signInWithPopup(auth, provider)
        .then((result) => {
          saveUser(result.user)
            .then((res) => {
              resolve(res);
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          console.log("signInWithGoogle error", error);
          reject(error);
        });
    });
  };

  const createUserWithEmail = (email, password) => {
    return new Promise((resolve, reject) => {
      createUserWithEmailAndPassword(auth, email, password)
        .then((result) => {
          saveUser(result.user)
            .then((res) => {
              resolve(res);
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          console.log("createUserWithEmail error", error);
          reject(error);
        });
    });
  };

  const signInWithEmail = (email, password) => {
    return new Promise((resolve, reject) => {
      signInWithEmailAndPassword(auth, email, password)
        .then((result) => {
          const u = result.user;

          axios
            .get(`/user/?email=${u.email}`)
            .then((res) => {
              const { data } = res;
              setUser(data);
              resolve(data);
            })
            .catch((error) => {
              console.log("getUser error", error);
              reject(error);
            });
        })
        .catch((error) => {
          console.log("signInWithEmail error", error);
          reject(error);
        });
    });
  };

  const signInWithApple = () => {
    return new Promise((resolve, reject) => {
      const provider = new OAuthProvider("apple.com");
      signInWithPopup(auth, provider)
        .then((result) => {
          saveUser(result.user)
            .then((res) => {
              resolve(res);
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          console.log("signInWithApple error", error);
          reject(error);
        });
    });
  };

  const saveUser = (u) => {
    return new Promise((resolve, reject) => {
      let providerId = u.providerData[0].providerId;
      let displayName = u.providerData[0].displayName;

      axios
        .post("/user/postUserPWA", {
          id_firebase: u.uid,
          email: u.email,
          name: displayName,
          platform_login: providerId,
          platform_origin: "pwa",
          original_language: window.navigator.language,
          set_language: i18n.language,
        })
        .then((res) => {
          const { data } = res;
          setUser(data.data);
          resolve(data.data);
        })
        .catch((error) => {
          reject(error);
          console.log("postUser error", error);
        });
    });
  };

  const updateUser = (user) => {
    return new Promise((resolve, reject) => {
      axios
        .put("/user/putUser", user)
        .then((res) => {
          const { data } = res;
          if (data.status) {
            setUser(data.data);
            resolve(data.data);
          }
        })
        .catch((error) => {
          reject(error);
          console.log("postUser error", error);
        });
    });
  };

  const signOut = () => {
    setUser(null);
    localStorage.removeItem("SPICY_USER");
    localStorage.removeItem("SPICY_PLAYER1");
    localStorage.removeItem("SPICY_PLAYER2");
    localStorage.removeItem("SPICY_LANG");
  };

  const getUser = ({ id, email }) => {
    return new Promise((resolve, reject) => {
      if (!id && !email) {
        resolve(null);
        return;
      }
      let filter = "";

      if (email) {
        filter = `?email=${email}`;
      }

      if (id) {
        filter = `?id=${id}`;
      }

      axios
        .get(`/user/${filter}`)
        .then((res) => {
          const { data } = res;

          const local_lang = localStorage.getItem("SPICY_LANG");

          if (data.set_language !== local_lang) {
            i18n.changeLanguage(data.set_language);
            localStorage.setItem("SPICY_LANG", data.set_language);
          }

          setUser(data);
          resolve(data);
        })
        .catch((error) => {
          console.log("getUser error", error);
          reject(error);
        });
    });
  };

  useEffect(() => {
    if (location.pathname === "/") {
      if (user) {
        navigate("/users");
      }
    } else {
      if (location.pathname === "/register") {
        return;
      }
      if (!user) {
        navigate("/");
      }
    }
  }, [user, location]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        getUser,
        updateUser,
        signInWithGoogle,
        createUserWithEmail,
        signInWithEmail,
        signInWithApple,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
